import React, { useState } from 'react';
import {
  Input,
  InputProps,
  FormLabel,
  FormControl,
  VStack,
  FormHelperText,
  IconButton,
  InputGroup,
  InputRightElement,
  FormErrorMessage,
} from '@chakra-ui/react';
import { css } from '@emotion/react';
import { EyeIcon } from '../../../assets/icons/eye';
import { EyeClosed } from '../../../assets/icons/eye-closed';
import { SearchIcon } from '../../../assets/icons/search-icon';

interface InputCustomProps extends InputProps {
  label?: string;
  errorMessage?: string | false | null;
  successMessage?: string;
  isDisabled?: boolean;
  hasSearchIcon?: boolean;
  borderColor?: string;
  backgroundColor?: string;
  spaceSize?: number;
  onSearchIconClick?: VoidFunction;
}

export const InputCustom: React.FC<InputCustomProps> = ({
  label,
  errorMessage,
  successMessage,
  isDisabled,
  hasSearchIcon,
  borderColor: customBorderColor,
  backgroundColor: customBackgroundColor,
  spaceSize,
  onSearchIconClick,
  ...props
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const errorColor = '#DC3545';
  const successColor = '#198754';
  const defaultColor = '#CED4DB';
  const disabledColor = '#CED4DA';
  const disabledBorderColor = '#999';

  const borderColor = errorMessage
    ? errorColor
    : successMessage
      ? successColor
      : isDisabled
        ? disabledColor
        : customBorderColor || defaultColor;

  const backgroundColor = isDisabled ? disabledColor : customBackgroundColor || 'white';

  // Определяем стили для фокуса
  const boxShadow = isFocused ? `0 0 5px 0 ${borderColor}` : 'none';

  // Генерируем стили для фокуса с помощью Emotion
  const focusStyles = css`
    &:focus,
    &:focus-visible,
    &[aria-invalid='true'],
    &[data-invalid],
    &[data-focus-visible] {
      border-color: ${borderColor};
      box-shadow: ${boxShadow};
    }
  `;

  return (
    <VStack spacing={spaceSize || 2} w="100%">
      {label && (
        <FormLabel textAlign="left" width="100%" margin={0}>
          {label}
        </FormLabel>
      )}
      <FormControl isInvalid={!!errorMessage} isDisabled={isDisabled}>
        <InputGroup>
          <Input
            {...props}
            type={showPassword ? 'text' : props.type}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            css={focusStyles}
            borderColor={borderColor}
            bg={backgroundColor}
            _hover={{ borderColor: borderColor }}
            _disabled={{ borderColor: disabledBorderColor, bg: disabledColor }}
            isInvalid={!!errorMessage}
            autoComplete={props.type === 'password' ? 'current-password' : 'off'}
          />

          {props.type === 'password' && (
            <InputRightElement>
              <IconButton
                variant="ghost"
                aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                icon={
                  showPassword ? <EyeIcon color={borderColor} /> : <EyeClosed color={borderColor} />
                }
                onClick={() => setShowPassword(!showPassword)}
                _hover={{ bg: 'transparent' }}
              />
            </InputRightElement>
          )}

          {hasSearchIcon && (
            <InputRightElement>
              <IconButton
                variant="ghost"
                aria-label="Поиск"
                icon={<SearchIcon color={borderColor} />}
                _hover={{ bg: 'transparent' }}
                onClick={onSearchIconClick}
              />
            </InputRightElement>
          )}
        </InputGroup>
        {errorMessage && <FormErrorMessage>{errorMessage}</FormErrorMessage>}
        {successMessage && <FormHelperText color={successColor}>{successMessage}</FormHelperText>}
      </FormControl>
    </VStack>
  );
};
